/**
 * KioskScreenPage – Manager control panel for the Kiosk/Terminal display.
 *
 * Lets managers manage:
 *   • Manager Board message
 *   • Stock 86 items (toggle in/out, add, delete)
 *   • Daily Specials (add with allergens, delete)
 *
 * Access: manager, gm, ops, super_admin
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { venueAPI, kioskAPI } from '../lib/api';
import { Loader2, Monitor, MessageSquare, ChefHat, Plus, Trash2, RefreshCw, Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// ── 14 Legal Allergens ──────────────────────────────────────────────────────
const ALLERGENS = [
  'Celery', 'Gluten', 'Crustaceans', 'Eggs', 'Fish',
  'Lupin', 'Milk', 'Molluscs', 'Mustard', 'Nuts',
  'Peanuts', 'Sesame', 'Soya', 'Sulphites',
];

// ── Venue selector ───────────────────────────────────────────────────────────
function VenueSelector({ venues, value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#18181B] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF6600] min-w-[180px]"
    >
      <option value="">Select venue…</option>
      {venues.map((v) => (
        <option key={v.id} value={v.id}>{v.name}</option>
      ))}
    </select>
  );
}

// ── Manager Board ────────────────────────────────────────────────────────────
function BoardSection({ venueId }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const r = await kioskAPI.getMessageBoard(venueId);
      setMessage(r.data?.message || '');
    } catch {
      toast.error('Failed to load manager board');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!venueId) return;
    setSaving(true);
    try {
      await kioskAPI.setMessageBoard(venueId, message);
      toast.success('Manager board updated');
    } catch {
      toast.error('Failed to save board');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#FF6600]" />
        <h2 className="text-white font-bold text-sm uppercase tracking-wide">Manager Board</h2>
        <p className="text-[#52525B] text-xs ml-auto">Visible on kiosk display at all times</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…
        </div>
      ) : (
        <>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Today's priorities, briefing notes, reminders for staff…"
            rows={5}
            className="w-full bg-[#0A0A0A] border border-[#3F3F46] rounded-xl p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6600] placeholder:text-[#52525B]"
          />
          <button
            onClick={save}
            disabled={saving}
            className="mt-3 flex items-center gap-2 bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-50 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save to Kiosk
          </button>
        </>
      )}
    </div>
  );
}

// ── Stock 86 ─────────────────────────────────────────────────────────────────
function Stock86Section({ venueId }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDept, setNewDept] = useState('kitchen');
  const [adding, setAdding]   = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const r = await kioskAPI.getStock86(venueId);
      setItems(r.data?.items || []);
    } catch {
      toast.error('Failed to load stock 86 items');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, current) => {
    try {
      await kioskAPI.updateStock86(venueId, id, !current);
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, available: !current } : it));
    } catch {
      toast.error('Failed to update item');
    }
  };

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const r = await kioskAPI.addStock86Item(venueId, newName.trim(), newDept);
      setItems((prev) => [...prev, r.data]);
      setNewName('');
      toast.success(`"${newName.trim()}" added`);
    } catch {
      toast.error('Failed to add item');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id, name) => {
    try {
      await kioskAPI.removeStock86Item(venueId, id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success(`"${name}" removed`);
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const kitchen = items.filter((i) => i.department === 'kitchen');
  const bar     = items.filter((i) => i.department === 'bar');

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <ChefHat className="w-5 h-5 text-[#FF6600]" />
        <h2 className="text-white font-bold text-sm uppercase tracking-wide">Stock 86</h2>
        <button onClick={load} className="ml-auto text-[#52525B] hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />Loading items…
        </div>
      ) : (
        <>
          {/* Kitchen items */}
          <div className="mb-4">
            <p className="text-[#71717A] text-xs font-semibold uppercase mb-2">🍳 Kitchen</p>
            {kitchen.length === 0
              ? <p className="text-[#3F3F46] text-xs italic">No kitchen items</p>
              : kitchen.map((it) => (
                <div key={it.id} className="flex items-center justify-between py-2.5 border-b border-[#27272A] last:border-0">
                  <span className={`text-sm font-medium ${it.available ? 'text-white' : 'text-[#71717A] line-through'}`}>
                    {it.name}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggle(it.id, it.available)}
                      className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${
                        it.available
                          ? 'bg-green-900/40 text-green-400 hover:bg-green-900/60'
                          : 'bg-red-900/40 text-red-400 hover:bg-red-900/60'
                      }`}
                    >
                      {it.available ? 'IN STOCK' : '86\'d'}
                    </button>
                    <button onClick={() => remove(it.id, it.name)} className="text-[#52525B] hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Bar items */}
          <div className="mb-4">
            <p className="text-[#71717A] text-xs font-semibold uppercase mb-2">🍹 Bar</p>
            {bar.length === 0
              ? <p className="text-[#3F3F46] text-xs italic">No bar items</p>
              : bar.map((it) => (
                <div key={it.id} className="flex items-center justify-between py-2.5 border-b border-[#27272A] last:border-0">
                  <span className={`text-sm font-medium ${it.available ? 'text-white' : 'text-[#71717A] line-through'}`}>
                    {it.name}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggle(it.id, it.available)}
                      className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${
                        it.available
                          ? 'bg-green-900/40 text-green-400 hover:bg-green-900/60'
                          : 'bg-red-900/40 text-red-400 hover:bg-red-900/60'
                      }`}
                    >
                      {it.available ? 'IN STOCK' : '86\'d'}
                    </button>
                    <button onClick={() => remove(it.id, it.name)} className="text-[#52525B] hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Add new item */}
          <div className="flex gap-2 pt-2 border-t border-[#27272A]">
            <select
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              className="bg-[#27272A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
            >
              <option value="kitchen">Kitchen</option>
              <option value="bar">Bar</option>
            </select>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              placeholder="Item name…"
              className="flex-1 bg-[#27272A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
            />
            <button
              onClick={add}
              disabled={adding || !newName.trim()}
              className="flex items-center gap-1.5 bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white font-bold text-sm px-4 py-2 rounded-xl transition-colors"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Daily Specials ────────────────────────────────────────────────────────────
const BLANK_SPECIAL = { name: '', description: '', price: '', allergens: [] };

function SpecialsSection({ venueId }) {
  const [specials, setSpecials] = useState([]);
  const [form, setForm]         = useState(BLANK_SPECIAL);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const r = await kioskAPI.getMenuSpecials(venueId);
      setSpecials(r.data?.specials || []);
    } catch {
      toast.error('Failed to load specials');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const toggleAllergen = (a) => {
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));
  };

  const add = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const r = await kioskAPI.addMenuSpecial(venueId, form);
      setSpecials((prev) => [r.data, ...prev]);
      setForm(BLANK_SPECIAL);
      toast.success(`"${form.name}" added to today's specials`);
    } catch {
      toast.error('Failed to add special');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    try {
      await kioskAPI.removeMenuSpecial(venueId, id);
      setSpecials((prev) => prev.filter((s) => s.id !== id));
      toast.success(`"${name}" removed`);
    } catch {
      toast.error('Failed to remove special');
    }
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Plus className="w-5 h-5 text-[#FF6600]" />
        <h2 className="text-white font-bold text-sm uppercase tracking-wide">Daily Specials</h2>
        <button onClick={load} className="ml-auto text-[#52525B] hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Active specials list */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />Loading specials…
        </div>
      ) : specials.length > 0 ? (
        <div className="space-y-2 mb-5">
          {specials.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 p-3 bg-[#27272A] rounded-xl">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold truncate">{s.name}</p>
                  {s.price && <span className="text-[#FF6600] font-bold text-sm shrink-0">£{s.price}</span>}
                </div>
                {s.description && <p className="text-[#71717A] text-xs mt-0.5 truncate">{s.description}</p>}
                {s.allergens?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {s.allergens.map((a) => (
                      <span key={a} className="bg-[#FF6600]/20 text-[#FF6600] text-xs px-1.5 py-0.5 rounded-full font-medium">
                        {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => remove(s.id, s.name)} className="text-[#52525B] hover:text-red-400 transition-colors shrink-0 mt-0.5">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#3F3F46] text-xs italic mb-4">No specials for today yet.</p>
      )}

      {/* Add form */}
      <div className="space-y-3 pt-4 border-t border-[#27272A]">
        <p className="text-[#A1A1AA] text-xs font-semibold uppercase tracking-wide">Add New Special</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Special name *"
            className="bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
          />
          <input
            value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
            placeholder="Price e.g. 12.50"
            className="bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
          />
        </div>
        <input
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Short description / upsell note (optional)"
          className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
        />

        {/* Allergen picker */}
        <div>
          <p className="text-[#71717A] text-xs uppercase font-semibold mb-2">Allergens (UK Food Law – tick all that apply)</p>
          <div className="flex flex-wrap gap-1.5">
            {ALLERGENS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggleAllergen(a)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  form.allergens.includes(a)
                    ? 'bg-[#FF6600] border-[#FF6600] text-white'
                    : 'bg-transparent border-[#3F3F46] text-[#71717A] hover:border-[#FF6600]/50 hover:text-white'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={add}
          disabled={saving || !form.name.trim()}
          className="flex items-center gap-2 bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add to Today's Specials
        </button>
      </div>
    </div>
  );
}

// ── Root Page ─────────────────────────────────────────────────────────────────
export default function KioskScreenPage() {
  const { user } = useAuthStore();
  const [venues, setVenues]         = useState([]);
  const [venueId, setVenueId]       = useState('');
  const [loadingVenues, setLoadingVenues] = useState(true);

  const canAccess = hasMinimumRole(user, ROLES.MANAGER);

  const visibleVenues = useMemo(() => {
    if (hasMinimumRole(user, ROLES.OPS)) return venues;
    return venues.filter((v) => (user?.venue_ids || []).includes(v.id));
  }, [venues, user]);

  useEffect(() => {
    venueAPI.list()
      .then((r) => {
        const list = r?.data || [];
        setVenues(list);
        if (list.length > 0) setVenueId(list[0].id);
      })
      .catch(() => toast.error('Failed to load venues'))
      .finally(() => setLoadingVenues(false));
  }, []);

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-[#52525B]">
        <AlertCircle className="w-10 h-10" />
        <p className="font-semibold">Manager access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6 pb-10">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Monitor className="w-6 h-6 text-[#FF6600]" />
            <h1 className="text-white text-2xl font-black uppercase tracking-tight">Kiosk Screen</h1>
          </div>
          <p className="text-[#52525B] text-sm">
            Manage what staff see on the terminal display — board, stock 86, and daily specials.
          </p>
        </div>

        {/* Venue selector */}
        {loadingVenues ? (
          <Loader2 className="w-5 h-5 text-[#FF6600] animate-spin shrink-0 mt-1" />
        ) : (
          <VenueSelector venues={visibleVenues} value={venueId} onChange={setVenueId} />
        )}
      </div>

      {!venueId ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm bg-[#18181B] rounded-2xl p-6 border border-[#27272A]">
          <AlertCircle className="w-4 h-4 shrink-0" />
          Select a venue above to manage its kiosk content.
        </div>
      ) : (
        <>
          <BoardSection   venueId={venueId} />
          <Stock86Section venueId={venueId} />
          <SpecialsSection venueId={venueId} />
        </>
      )}
    </div>
  );
}
